import { useEffect, useState, useMemo, useRef } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import ScrollElement from '../../../global/Elements/ScrollElement';
import { searchContext, userAppContext } from '../../../context/Context';
import { scroller } from 'react-scroll';
import { ScoreBreakdownPanel } from '@/components/ScoreBreakdown/ScoreBreakdownPanel';
import { useScoreBreakdownStore } from '@/store/scoreBreakdownStore';
import { usePrevalenceBreakdownStore } from '@/store/prevalenceBreakdownStore';
import { PrevalenceBreakdownPanel } from '@/components/PrevalenceBreakdown/PrevalenceBreakdownPanel';
import EvidenceTimelinePanel from '@/components/EvidenceTimeline/EvidenceTimelinePanel';
import { useEvidenceTimelineStore } from '@/store/evidenceTimelineStore';

import ManageGrid from './components/table/ManageGrid';
import ManageGraph from './components/table/ManageGraph';

import SummaryGDAGraphs from '@/components/Graphs/SummaryGDA/SummaryGDAGraphs';
import SummaryVDAGraphs from '@/components/Graphs/SummaryVDA/SummaryVDAGraphs';

import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactSlider from 'react-slider';
import MappingResults from '@/components/Mapping/MappingResults';
import { MappingDrillDownProvider } from '@/context/MappingDrillDownContext';
import DrillDownTableContainer from '@/components/Mapping/DrillDownTableContainer';
import { SearchContext, UserContext } from '../../../types';
import SharedButton from '../../../global/Button/Button';
import { getTabList } from './helpers/tableHelpers';
import DDAGraphs from '@/components/Graphs/DDA/DDAGraphs';
import { trackBrowseTabClick } from '../../../utils/analytics';
import ScoreModal from './components/modal/ScoreModal';
import { AdjustmentsHorizontalIcon, ChevronDoubleLeftIcon } from '@heroicons/react/16/solid';
import { deleteFilter } from '../../../services/savedFiltersService';
import updateParams from '../../../utils/updateParams';
import { mapItemsToSearchItems } from '../../../components/Table/types/index';
import { useRightSidebarLayout } from './context/RightSidebarLayoutContext';
import SideBarContent from '@/components/QueriedContent/SideBarContent';
import GeneInfoPanelContent from '@/components/GeneContent/GeneInfoPanelContent';
import { RightAsideSidebar } from './components/table/components/RightAsideSidebar';
import { NAVBAR_HEIGHT_PX } from '@/router/AppRouter';
import { MappingSidebarColumnProvider, useMappingSidebarColumn } from '@/context/MappingSidebarColumnContext';
import { useMappingStackStore, useCurrentMappingLevel } from '@/store/mappingStackStore';
import useScrollWindow from '@/hooks/useScrollWindow';
import { useAlertSystem } from './components/table/hooks';
import { useTableStore } from '@/store/tableStore';
import {
  MAPPING_URL_PARAM,
  decodeMappingStateFromUrl,
  encodeMappingStateForUrl,
} from '@/utils/mappingUrlState';
import SharedBadge from '@/global/Badge/Badge';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';
import { getTitle, getName, DATASETS } from '@/components/Constants';
import { CheckIcon, ChevronDownIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';

const SHOW_HISTORIAL_SCORE = process.env.REACT_APP_SHOW_HISTORIAL_SCORE === 'true';

function getSearchItemBadgeLabel(item: any): { label: string; tooltipId: string } {
  const title = getTitle(item);
  const id = getName(item);
  const titleStr =
    typeof title === 'object' && title !== null && 'title' in title
      ? String((title as { title?: string }).title ?? id)
      : String(title ?? '');
  const idStr = String(id ?? item?.id ?? '');
  const isAllDataset =
    typeof title === 'object' &&
    title !== null &&
    'title' in (title as object) &&
    (title as { title?: string }).title === DATASETS.ALL;
  const label = titleStr && !isAllDataset ? titleStr : idStr;
  return { label, tooltipId: idStr };
}

function getNumericScoreFromSearchItem(item: any): number | null {
  const keys = ['score', 'matchScore', 'relevance', 'relevanceScore'] as const;
  for (const k of keys) {
    const v = item?.[k];
    if (v == null || v === '') continue;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function sortSearchItemsForDisplay(items: any[]): any[] {
  if (!items?.length) return [];
  const decorated = items.map(item => ({
    item,
    ...getSearchItemBadgeLabel(item),
  }));
  const anyScore = decorated.some(d => getNumericScoreFromSearchItem(d.item) != null);
  if (anyScore) {
    return [...decorated]
      .sort((a, b) => {
        const sa = getNumericScoreFromSearchItem(a.item);
        const sb = getNumericScoreFromSearchItem(b.item);
        if (sa != null && sb != null) return sb - sa;
        if (sa != null) return -1;
        if (sb != null) return 1;
        return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
      })
      .map(d => d.item);
  }
  return [...decorated]
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
    .map(d => d.item);
}

function getEntityPluralLabel(view: string | undefined, items: any[]): string {
  const byDataset: Record<string, string> = {
    DISEASES: 'diseases',
    GENES: 'genes',
    VARIANTS: 'variants',
    CHEMICALS: 'chemicals',
  };
  if (view && view !== 'ALL') {
    return byDataset[view] ?? 'items';
  }
  const datasets = new Set(items.map(i => i?.dataset).filter(Boolean) as string[]);
  if (datasets.size === 1) {
    const only = [...datasets][0];
    return byDataset[only] ?? 'items';
  }
  return 'items';
}

interface SearchResultsProps {
  state: any;
  dispatch: any;
}

type ScoreHistoryPanelState = {
  selectedYear: number | null;
  selectedYearFrom: number | null;
  selectedYearTo: number | null;
  yearInput: string;
  yearFromInput: string;
  yearToInput: string;
  tab?: string;
};

function SearchResultsInner({ state, dispatch }: SearchResultsProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { view, source, tab, searchItems, setSearchItems, geneContent, setGeneContent, mappingContent } =
    searchContext() as unknown as SearchContext;
  const { isAuthenticated, groups, restrictChemical } = userAppContext() as unknown as UserContext;
  const [isInfoSidebarOpen, setIsInfoSidebarOpen] = useState(false);
  const { showSuccessAlert } = useAlertSystem();
  const savedColumsApplied = useTableStore(s => s.savedColumsApplied);
  const setSavedColumsApplied = useTableStore(s => s.setSavedColumsApplied);

  const scoreBreakdownRow = useScoreBreakdownStore(s => s.scoreBreakdownRow);
  const setScoreBreakdownRow = useScoreBreakdownStore(s => s.setScoreBreakdownRow);
  const prevalenceBreakdownRow = usePrevalenceBreakdownStore(s => s.prevalenceBreakdownRow);
  const setPrevalenceBreakdownRow = usePrevalenceBreakdownStore(s => s.setPrevalenceBreakdownRow);
  const evidenceTimelineRow = useEvidenceTimelineStore(s => s.evidenceTimelineRow);
  const setEvidenceTimelineRow = useEvidenceTimelineStore(s => s.setEvidenceTimelineRow);

  useEffect(() => {
    if (savedColumsApplied) {
      showSuccessAlert(`Column configuration "${savedColumsApplied.name}" applied successfully`);
      setSavedColumsApplied(null);
    }
  }, [savedColumsApplied]);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedView, setSelectedView] = useState<'tables' | 'graphs'>(() => {
    const display = searchParams.get('display');
    return display === 'graphs' ? 'graphs' : 'tables';
  });
  const displayParam = searchParams.get('display');
  useEffect(() => {
    setSelectedView(displayParam === 'graphs' ? 'graphs' : 'tables');
  }, [displayParam]);
  const [defaultFilterValue, _setDefaultFilterValue] = useState([]);
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [refreshScoresDropdown, setRefreshScoresDropdown] = useState(0);
  const [leftInfoPanelItem, setLeftInfoPanelItem] = useState<any>(null);
  const [leftSentencePanel, setLeftSentencePanel] = useState<{ content: string; allowCopy: boolean } | null>(
    null,
  );
  const [leftScoreHistoryPanel, setLeftScoreHistoryPanel] = useState<ScoreHistoryPanelState | null>(null);
  const [sentenceCopied, setSentenceCopied] = useState(false);
  const sentenceToCopyRef = useRef<HTMLDivElement | null>(null);
  const [sourceWeights, setSourceWeights] = useState<Array<{ name: string; score: number }>>(() => {
    const stored = sessionStorage.getItem(`sourceWeights_${tab}`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  const isTrial = groups?.includes('ROLE_TRIAL');
  const isAcademic = groups?.includes('ROLE_ACADEMIC');
  const { isSidebarOpen, sidebarWidth, setSidebarOpen, expandSearchItemsSection } = useRightSidebarLayout();
  const [searchItemsDropdownOpen, setSearchItemsDropdownOpen] = useState(false);
  const searchItemsDropdownRef = useRef<HTMLDivElement>(null);
  const [graphsColumnModel, setGraphsColumnModel] = useState<Record<string, boolean>>({});

  const { isMobile, isTablet } = useScrollWindow();
  const isDesktop = !isMobile && !isTablet;

  const [summaryColumnModel, setSummaryColumnModel] = useState<Record<string, boolean> | undefined>(
    undefined,
  );
  const [summaryColumnOrder, setSummaryColumnOrder] = useState<string[] | undefined>(undefined);
  const [evidenceColumnModel, setEvidenceColumnModel] = useState<Record<string, boolean> | undefined>(
    undefined,
  );
  const [evidenceColumnOrder, setEvidenceColumnOrder] = useState<string[] | undefined>(undefined);
  const [activeColumnPanel, setActiveColumnPanel] = useState<'summary' | 'evidence'>('summary');

  const useDrillDownMapping = process.env.REACT_APP_USE_DRILLDOWN_MAPPING === 'true';
  const mappingStackCurrentIndex = useMappingStackStore(s => s.currentIndex);
  const mappingStackLength = useMappingStackStore(s => s.stack.length);
  const mappingStack = useMappingStackStore(s => s.stack);
  const mappingTableStateByLevelId = useMappingStackStore(s => s.tableStateByLevelId);
  const clearMappingStack = useMappingStackStore(s => s.clearFromLevel);
  const collapseMappingToRoot = useMappingStackStore(s => s.collapseToRootLevel);
  const currentMappingLevel = useCurrentMappingLevel();

  const mappingUrlRestoreDoneRef = useRef(false);
  const skipMappingUrlParamStripRef = useRef(false);
  const [isMappingUrlHydrated, setIsMappingUrlHydrated] = useState(false);

  useEffect(() => {
    if (mappingUrlRestoreDoneRef.current) return;
    mappingUrlRestoreDoneRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const encoded = params.get(MAPPING_URL_PARAM);
    if (!encoded) {
      setIsMappingUrlHydrated(true);
      return;
    }

    const decoded = decodeMappingStateFromUrl(encoded);
    if (!decoded || decoded.stack.length === 0) {
      setIsMappingUrlHydrated(true);
      return;
    }

    useMappingStackStore.setState({
      stack: decoded.stack,
      currentIndex: decoded.currentIndex,
      tableStateByLevelId: decoded.tableStateByLevelId ?? {},
    });

    setTimeout(() => setIsMappingUrlHydrated(true), 0);
  }, []);

  useEffect(() => {
    if (!isMappingUrlHydrated) return;

    const params = new URLSearchParams(window.location.search);
    const current = params.get(MAPPING_URL_PARAM);

    const hasDrillDown = mappingStack.length > 1 && mappingStackCurrentIndex > 0;
    if (!hasDrillDown) {
      if (skipMappingUrlParamStripRef.current) {
        queueMicrotask(() => {
          skipMappingUrlParamStripRef.current = false;
        });
        return;
      }
      if (current != null) {
        params.delete(MAPPING_URL_PARAM);
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}?${params.toString()}${window.location.hash}`,
        );
      }
      return;
    }

    const encoded = encodeMappingStateForUrl({
      stack: mappingStack,
      currentIndex: mappingStackCurrentIndex,
      tableStateByLevelId: mappingTableStateByLevelId,
    });
    if (current === encoded) return;

    params.set(MAPPING_URL_PARAM, encoded);
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}?${params.toString()}${window.location.hash}`,
    );
  }, [mappingStack, mappingStackCurrentIndex, mappingTableStateByLevelId, isMappingUrlHydrated]);

  useEffect(() => {
    return () => {
      setScoreBreakdownRow(null);
      setPrevalenceBreakdownRow(null);
      setEvidenceTimelineRow(null);
    };
  }, []);

  const mappingSidebar = useMappingSidebarColumn();
  const isInMappingView = useDrillDownMapping && mappingStackLength > 0 && mappingStackCurrentIndex > 0;
  const useMappingSidebarState = isInMappingView && mappingSidebar.state != null;

  const columnOrder = useMappingSidebarState
    ? mappingSidebar.state!.columnOrder
    : activeColumnPanel === 'summary'
      ? summaryColumnOrder
      : evidenceColumnOrder;

  const summaryResetRef = useRef<(() => void) | null>(null);
  const evidenceResetRef = useRef<(() => void) | null>(null);

  const handleResetToDefault = () => {
    if (useMappingSidebarState && mappingSidebar.state) {
      mappingSidebar.state.setColumnVisibilityModel(() => ({}));
      mappingSidebar.state.setColumnOrder([]);
    } else if (activeColumnPanel === 'summary') {
      summaryResetRef.current?.();
    } else {
      evidenceResetRef.current?.();
    }
  };

  const handleApplyWeights = (weights: Array<{ name: string; score: number }>) => {
    setSourceWeights(weights);
    if (weights.length > 0) {
      sessionStorage.setItem(`sourceWeights_${tab}`, JSON.stringify(weights));
    } else {
      sessionStorage.removeItem(`sourceWeights_${tab}`);
    }
  };

  const handleApplySavedScore = (savedScore: any) => {
    if (savedScore.sourceWeights && savedScore.sourceWeights.length > 0) {
      handleApplyWeights(savedScore.sourceWeights);

      const urlParams = new URLSearchParams(window.location.search);
      if (savedScore.sourceWeights.length > 1) {
        urlParams.set('source', 'ALL');
      } else if (savedScore.sourceWeights.length === 1) {
        urlParams.set('source', savedScore.sourceWeights[0].name);
      }

      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.pushState({}, '', newUrl);

      window.location.reload();
    }
  };

  const handleDeleteSavedScore = async (scoreId: string) => {
    try {
      if (!scoreId.startsWith('savedScore_')) {
        await deleteFilter(scoreId);
      }
    } catch (error) {
      console.error('Error deleting saved score:', error);
    }
  };

  const handleClearWeights = () => {
    // Clear weights
    setSourceWeights([]);
    sessionStorage.removeItem(`sourceWeights_${tab}`);

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('source') === 'ALL') {
      // Could set to a default source, but for now just reload
    }

    window.location.reload();
  };

  const handleApplySavedColumns = (savedColumn: any) => {
    if (savedColumn.columnVisibilityModel) {
      if (activeColumnPanel === 'summary') {
        setSummaryColumnModel(savedColumn.columnVisibilityModel);
      } else {
        setEvidenceColumnModel(savedColumn.columnVisibilityModel);
      }
    }
    if (savedColumn.columnOrder) {
      if (activeColumnPanel === 'summary') {
        setSummaryColumnOrder(savedColumn.columnOrder);
      } else {
        setEvidenceColumnOrder(savedColumn.columnOrder);
      }
    }
    setSavedColumsApplied({
      name: savedColumn.name,
      columnVisibilityModel: savedColumn.columnVisibilityModel,
      columnOrder: savedColumn.columnOrder,
    });
  };

  const handleDeleteSavedColumn = async (columnId: string) => {
    try {
      if (!columnId.startsWith('columnSelection_')) {
        await deleteFilter(columnId);
      }
    } catch (error) {
      console.error('Error deleting column configuration:', error);
    }
  };

  const tabList = useMemo(() => {
    return getTabList(view as string, tab as string);
  }, [view, tab]);

  const sortedSearchItemsForHeader = useMemo(
    () => sortSearchItemsForDisplay(searchItems ?? []),
    [searchItems],
  );
  const searchItemsEntityPlural = useMemo(
    () => getEntityPluralLabel(view as string | undefined, searchItems ?? []),
    [view, searchItems],
  );
  const searchItemsHaveScore = useMemo(
    () => (searchItems ?? []).some(i => getNumericScoreFromSearchItem(i) != null),
    [searchItems],
  );

  useEffect(() => {
    if (!searchItemsDropdownOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (searchItemsDropdownRef.current && !searchItemsDropdownRef.current.contains(e.target as Node)) {
        setSearchItemsDropdownOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSearchItemsDropdownOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [searchItemsDropdownOpen]);

  const handleRemoveSearchItem = (index: number) => {
    const remaining = (searchItems ?? []).filter((_, i) => i !== index);
    if (remaining.length === 0) {
      skipMappingUrlParamStripRef.current = true;
      navigate('/', { replace: true });
      clearMappingStack(0);
    } else {
      const params = new URLSearchParams(window.location.search);
      const newParams = updateParams({
        searchItems: remaining as any,
        view: params.get('view') ?? undefined,
        source: params.get('source') ?? undefined,
        tab: params.get('tab') ?? undefined,
      });
      navigate({ pathname: window.location.pathname, search: newParams });
    }
  };

  const handleTabSelect = (index: number) => {
    // Simply proceed with tab change - weights are now tab-specific
    proceedWithTabChange(index);
  };

  const proceedWithTabChange = (index: number) => {
    setTabIndex(index);
    const newTab = tabList[index].key;
    const tabLabel = tabList[index].label;

    trackBrowseTabClick(tabLabel);

    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('tab', newTab);

    navigate({ pathname: window.location.pathname, search: `?${currentParams.toString()}` });
  };

  useEffect(() => {
    // Update nav component state based on app state
    const newIndex = tabList.findIndex(t => t.key === tab);
    setTabIndex(newIndex);

    // Load weights for the new tab
    const stored = sessionStorage.getItem(`sourceWeights_${tab}`);
    if (stored) {
      try {
        setSourceWeights(JSON.parse(stored));
      } catch (e) {
        setSourceWeights([]);
      }
    } else {
      setSourceWeights([]);
    }
  }, [tab, tabList]);

  // Reset controlled column models when the tab actually changes
  const prevTabForResetRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevTabForResetRef.current === null || prevTabForResetRef.current === tab) {
      prevTabForResetRef.current = tab as string;
      return;
    }
    prevTabForResetRef.current = tab as string;
    setSummaryColumnModel(undefined);
    setEvidenceColumnModel(undefined);
    setSummaryColumnOrder(undefined);
    setEvidenceColumnOrder(undefined);
  }, [tab]);

  const prevTabForMappingRef = useRef<string | null>(null);
  useEffect(() => {
    const current = tab as string;
    if (prevTabForMappingRef.current !== null && prevTabForMappingRef.current !== current) {
      collapseMappingToRoot();
    }
    prevTabForMappingRef.current = current;
  }, [tab, collapseMappingToRoot]);

  // Table/Graph button
  // Button is only shown on Association views, not on entity list tabs (DISEASES, GENES, VARIANTS, CHEMICALS)
  const entityListTabs = ['DISEASES', 'GENES', 'VARIANTS', 'CHEMICALS'];
  const showManageGraph = view !== 'ALL' && view !== 'CHEMICALS' && !entityListTabs.includes(tab as string);
  const visibleTabs = selectedView === 'graphs' ? tabList.slice(0, -1) : tabList;

  // Grid view items
  const urlParams = new URLSearchParams(window.location.search);
  const isEvidenceParam = urlParams.get('isEvidence');

  let showSummary = true;
  let showEvidence = true;

  if (isEvidenceParam === 'false') {
    showEvidence = false;
  } else if (isEvidenceParam === 'true') {
    showSummary = false;
  } else {
    if (tab === 'DDA') showEvidence = false;
    // Entity list tabs (DISEASES, GENES, VARIANTS, CHEMICALS) only show summary table
    if (['DISEASES', 'GENES', 'VARIANTS', 'CHEMICALS'].includes(tab as string)) showEvidence = false;
    if (view === 'ALL' && !['GDA', 'VDA'].includes(tab as string)) showEvidence = false;
  }

  if (entityListTabs.includes(tab as string)) {
    showEvidence = false;
    showSummary = true;
  }
  if (view === 'ALL' && !['GDA', 'VDA'].includes(tab as string)) {
    showEvidence = false;
    showSummary = true;
  }

  useEffect(() => {
    if (!showEvidence) setActiveColumnPanel('summary');
  }, [showEvidence]);

  // Graph view items
  const graphMap = {
    GDA: SummaryGDAGraphs,
    VDA: SummaryVDAGraphs,
    DDA: DDAGraphs,
  };

  useEffect(() => {
    if (scoreBreakdownRow) {
      setLeftInfoPanelItem(null);
      setLeftSentencePanel(null);
      setPrevalenceBreakdownRow(null);
      setLeftScoreHistoryPanel(null);
    }
  }, [scoreBreakdownRow]);

  useEffect(() => {
    if (prevalenceBreakdownRow) {
      setLeftInfoPanelItem(null);
      setLeftSentencePanel(null);
      setScoreBreakdownRow(null);
      setLeftScoreHistoryPanel(null);
    }
  }, [prevalenceBreakdownRow]);

  const handleOpenEntityInfo = (item: any) => {
    setScoreBreakdownRow(null);
    setPrevalenceBreakdownRow(null);
    setLeftSentencePanel(null);
    setLeftScoreHistoryPanel(null);
    setLeftInfoPanelItem(item);
  };

  const handleCopySentenceFromPanel = async () => {
    if (!sentenceToCopyRef.current) return;
    try {
      await navigator.clipboard.writeText(sentenceToCopyRef.current.textContent || '');
      setSentenceCopied(true);
      setTimeout(() => setSentenceCopied(false), 3000);
    } catch {
      // Silent error handling for copy operation
    }
  };

  useEffect(() => {
    const handleOpenSentencePanel = (event: Event) => {
      const customEvent = event as CustomEvent<{ content?: string; allowCopy?: boolean }>;
      const content = customEvent.detail?.content;
      if (!content) return;
      setScoreBreakdownRow(null);
      setPrevalenceBreakdownRow(null);
      setEvidenceTimelineRow(null);
      setLeftInfoPanelItem(null);
      setLeftScoreHistoryPanel(null);
      setLeftSentencePanel({
        content,
        allowCopy: Boolean(customEvent.detail?.allowCopy),
      });
    };

    window.addEventListener('disgenet:open-left-sentence-panel', handleOpenSentencePanel);
    return () => window.removeEventListener('disgenet:open-left-sentence-panel', handleOpenSentencePanel);
  }, []);

  useEffect(() => {
    if (!SHOW_HISTORIAL_SCORE) return;
    const handleOpenScoreHistoryPanel = (event: Event) => {
      const customEvent = event as CustomEvent<{
        selectedYear?: number | null;
        selectedYearFrom?: number | null;
        selectedYearTo?: number | null;
        tab?: string;
      }>;
      const selectedYear =
        typeof customEvent.detail?.selectedYear === 'number' ? customEvent.detail.selectedYear : null;
      const selectedYearFrom =
        typeof customEvent.detail?.selectedYearFrom === 'number' ? customEvent.detail.selectedYearFrom : null;
      const selectedYearTo =
        typeof customEvent.detail?.selectedYearTo === 'number' ? customEvent.detail.selectedYearTo : null;

      setScoreBreakdownRow(null);
      setPrevalenceBreakdownRow(null);
      setEvidenceTimelineRow(null);
      setLeftInfoPanelItem(null);
      setLeftSentencePanel(null);

      setLeftScoreHistoryPanel({
        selectedYear,
        selectedYearFrom,
        selectedYearTo,
        yearInput: selectedYear != null ? String(selectedYear) : '',
        yearFromInput: selectedYearFrom != null ? String(selectedYearFrom) : '',
        yearToInput: selectedYearTo != null ? String(selectedYearTo) : '',
        tab: customEvent.detail?.tab,
      });
    };

    window.addEventListener('disgenet:open-score-history-panel', handleOpenScoreHistoryPanel);
    return () => window.removeEventListener('disgenet:open-score-history-panel', handleOpenScoreHistoryPanel);
  }, []);

  const handleClearAllSearchItems = () => {
    skipMappingUrlParamStripRef.current = true;
    navigate('/', { replace: true });
    clearMappingStack(0);
  };

  const prevSearchItemsLengthRef = useRef<number | null>(null);
  useEffect(() => {
    if (
      prevSearchItemsLengthRef.current !== null &&
      prevSearchItemsLengthRef.current > 0 &&
      searchItems?.length === 0
    ) {
      setSelectedView('tables');
      setSearchParams(
        prev => {
          const u = new URLSearchParams(prev);
          u.delete('display');
          return u;
        },
        { replace: true },
      );
    }
    prevSearchItemsLengthRef.current = searchItems?.length ?? 0;
  }, [searchItems]);

  // scroll to ref animation
  const scrollToRef = (ref: string) => {
    scroller.scrollTo(ref, {
      duration: 750,
      // delay: 50,
      // smooth: 'easeInOutQuad',
      smooth: true,
    });
  };

  useEffect(() => {
    if (geneContent?.isActive && geneContent.geneInfo?.id) {
      handleOpenEntityInfo({
        id: geneContent.geneInfo.id,
        symbol: geneContent.geneInfo.symbol,
        entityView: 'GENES',
      });
      setGeneContent({ isActive: false, geneInfo: null });
    }
  }, [geneContent?.isActive]);

  useEffect(() => {
    if (mappingContent!.isActive) {
      scrollToRef('Mapping');
    }
  }, [mappingContent!.isActive]);

  // Auto-scroll to Evidence table if URL has only Evidence filters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let hasEvidenceFilters = false;
    let hasSummaryFilters = false;

    for (const [key] of params.entries()) {
      if (key.startsWith('filters-evidence-') || key.startsWith('filters[evidence][')) {
        hasEvidenceFilters = true;
      }
      if (key.startsWith('filters-summary-') || key.startsWith('filters[summary][')) {
        hasSummaryFilters = true;
      }
    }

    if (hasEvidenceFilters && !hasSummaryFilters && showEvidence) {
      // Small delay to ensure the component is mounted
      setTimeout(() => {
        scrollToRef('Evidence');
      }, 300);
    }
  }, [showEvidence]);

  return (
    <>
      {/* Score Modal */}
      <ScoreModal
        isOpen={scoreModalOpen}
        onClose={() => setScoreModalOpen(false)}
        onApplyWeights={handleApplyWeights}
        onScoreSaved={() => setRefreshScoresDropdown(prev => prev + 1)}
        refreshTrigger={refreshScoresDropdown}
      />

      {isTrial && (
        <div className="info-container p-2 text-center">
          <p style={{ fontSize: '18px', color: 'rgb(190,0,128)', paddingTop: '10px' }}>
            Only the top 30 results are shown.{' '}
            <a href="/academic-plans" style={{ color: '#0d1661' }}>
              Upgrade your account{' '}
            </a>{' '}
            to see all results.{' '}
            <a href="/plans" style={{ color: '#0d1661' }}>
              View our plans
            </a>
            .
          </p>
        </div>
      )}
      {leftInfoPanelItem && (
        <aside
          role="complementary"
          aria-label="Item details"
          className="fixed left-0 z-40 flex flex-col border-r border-gray-200 bg-white shadow-lg overflow-hidden"
          style={{
            top: isDesktop ? NAVBAR_HEIGHT_PX : '3.3rem',
            height: `calc(100vh - ${NAVBAR_HEIGHT_PX}px)`,
            width: isDesktop ? 340 : '100vw',
            minWidth: isDesktop ? 340 : '100vw',
          }}
        >
          {leftInfoPanelItem?.entityView === 'GENES' ? (
            <GeneInfoPanelContent
              geneId={String(leftInfoPanelItem.id ?? leftInfoPanelItem.geneId ?? '')}
              geneSymbol={String(leftInfoPanelItem.symbol ?? leftInfoPanelItem.geneSymbol ?? '')}
              onClose={() => setLeftInfoPanelItem(null)}
            />
          ) : (
            <SideBarContent
              selectedInfoItem={leftInfoPanelItem}
              view={view}
              showModalExample={false}
              setShowModalExample={() => {}}
              renderInPanel
              onPanelClose={() => setLeftInfoPanelItem(null)}
            />
          )}
        </aside>
      )}
      {leftSentencePanel && !leftInfoPanelItem && (
        <aside
          role="complementary"
          aria-label="Sentence details"
          className="fixed left-0 z-40 flex flex-col border-r border-gray-200 bg-white shadow-lg overflow-hidden"
          style={{
            top: isDesktop ? NAVBAR_HEIGHT_PX : '3.3rem',
            height: isDesktop ? `calc(100vh - ${NAVBAR_HEIGHT_PX}px)` : '44vh',
            width: isDesktop ? 340 : '100vw',
            minWidth: isDesktop ? 340 : '100vw',
          }}
        >
          <div className="flex items-center justify-between flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="text-sm font-semibold text-gray-900 truncate min-w-0 mb-0">Sentence Details</h2>
            <button
              type="button"
              onClick={() => setLeftSentencePanel(null)}
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-200 flex-shrink-0"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 min-h-0">
            <div
              ref={sentenceToCopyRef}
              dangerouslySetInnerHTML={{ __html: leftSentencePanel.content }}
              className="text-secondary font-weight-bold"
            />
            {leftSentencePanel.allowCopy && (
              <div className="flex justify-start mt-3">
                <SharedButton
                  size="xs"
                  variant="secondary"
                  onClick={handleCopySentenceFromPanel}
                  className="ml-0 mt-0"
                >
                  {sentenceCopied ? (
                    <CheckIcon className="w-4 h-4 mr-1" strokeWidth={2} />
                  ) : (
                    <ClipboardDocumentIcon className="w-4 h-4 mr-1" strokeWidth={2} />
                  )}
                  {sentenceCopied ? ' Copied' : ' Copy sentence'}
                </SharedButton>
              </div>
            )}
          </div>
        </aside>
      )}
      {SHOW_HISTORIAL_SCORE && leftScoreHistoryPanel && !leftInfoPanelItem && !leftSentencePanel && (
        <aside
          role="complementary"
          aria-label="Score history"
          className="fixed left-0 z-40 flex flex-col border-r border-gray-200 bg-white shadow-lg overflow-hidden"
          style={{
            top: isDesktop ? NAVBAR_HEIGHT_PX : '3.3rem',
            height: isDesktop ? `calc(100vh - ${NAVBAR_HEIGHT_PX}px)` : '44vh',
            width: isDesktop ? 340 : '100vw',
            minWidth: isDesktop ? 340 : '100vw',
          }}
        >
          {(() => {
            const currentYear = new Date().getFullYear();
            const minAllowedYear = 1900;
            const suggestedYears = [
              currentYear,
              currentYear - 1,
              currentYear - 2,
              currentYear - 5,
              2015,
              2010,
              2000,
            ].filter((year, index, array) => year >= minAllowedYear && array.indexOf(year) === index);
            const inputValue = leftScoreHistoryPanel.yearInput;
            const inputFromValue = leftScoreHistoryPanel.yearFromInput;
            const inputToValue = leftScoreHistoryPanel.yearToInput;

            const applyYear = (year: number | null) => {
              setLeftScoreHistoryPanel(prev =>
                prev
                  ? {
                      ...prev,
                      selectedYear: year,
                      selectedYearFrom: null,
                      selectedYearTo: null,
                      yearInput: year != null ? String(year) : '',
                      yearFromInput: '',
                      yearToInput: '',
                    }
                  : prev,
              );
              window.dispatchEvent(
                new CustomEvent('disgenet:apply-score-history-year', { detail: { year } }),
              );
            };

            const applyYearRange = (yearFrom: number | null, yearTo: number | null) => {
              setLeftScoreHistoryPanel(prev =>
                prev
                  ? {
                      ...prev,
                      selectedYear: null,
                      yearInput: '',
                      selectedYearFrom: yearFrom,
                      selectedYearTo: yearTo,
                      yearFromInput: yearFrom != null ? String(yearFrom) : '',
                      yearToInput: yearTo != null ? String(yearTo) : '',
                    }
                  : prev,
              );
              window.dispatchEvent(
                new CustomEvent('disgenet:apply-score-history-year', { detail: { yearFrom, yearTo } }),
              );
            };

            const tryApplyInputYear = () => {
              const raw = inputValue.trim();
              if (raw === '') {
                applyYear(null);
                return;
              }
              const parsed = Number(raw);
              if (!Number.isFinite(parsed)) return;
              const normalized = Math.trunc(parsed);
              if (normalized < minAllowedYear || normalized > currentYear) return;
              applyYear(normalized);
            };

            const tryApplyInputYearRange = () => {
              const rawFrom = inputFromValue.trim();
              const rawTo = inputToValue.trim();
              if (rawFrom === '' || rawTo === '') return;

              const parsedFrom = Number(rawFrom);
              const parsedTo = Number(rawTo);
              if (!Number.isFinite(parsedFrom) || !Number.isFinite(parsedTo)) return;

              const normalizedFrom = Math.trunc(parsedFrom);
              const normalizedTo = Math.trunc(parsedTo);
              if (normalizedFrom < minAllowedYear || normalizedTo < minAllowedYear) return;
              if (normalizedFrom > currentYear || normalizedTo > currentYear) return;
              if (normalizedFrom > normalizedTo) return;

              applyYearRange(normalizedFrom, normalizedTo);
            };

            const rangeFrom = leftScoreHistoryPanel.selectedYearFrom ?? minAllowedYear;
            const rangeTo = leftScoreHistoryPanel.selectedYearTo ?? currentYear;

            return (
              <>
                <div className="flex items-center justify-between flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-sm font-semibold text-gray-900 truncate min-w-0 mb-0">Score history</h2>
                  <button
                    type="button"
                    onClick={() => setLeftScoreHistoryPanel(null)}
                    className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-200 flex-shrink-0"
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-3 min-h-0">
                  <p className="text-xs text-gray-600 mb-2">
                    Select a year to emulate score calculation with evidence from that year.
                  </p>
                  <label
                    htmlFor="left-score-year-input"
                    className="text-xs font-medium text-gray-700 mb-1 block"
                  >
                    Score year
                  </label>
                  <input
                    id="left-score-year-input"
                    type="number"
                    min={minAllowedYear}
                    max={currentYear}
                    placeholder={`e.g. ${currentYear - 1}`}
                    className="filter-input w-full !mt-0"
                    value={inputValue}
                    onChange={e => {
                      const rawValue = e.target.value;
                      setLeftScoreHistoryPanel(prev => (prev ? { ...prev, yearInput: rawValue } : prev));
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        tryApplyInputYear();
                      }
                    }}
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="rounded-full border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                      onClick={() => applyYear(null)}
                    >
                      Latest
                    </button>
                    {suggestedYears.map(year => (
                      <button
                        key={year}
                        type="button"
                        className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-800 hover:bg-violet-100"
                        onClick={() => applyYear(year)}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                  <div className="mt-4 border-t border-gray-200 pt-3">
                    <p className="mb-2 text-xs font-medium text-gray-700">Or choose a range</p>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        min={minAllowedYear}
                        max={currentYear}
                        placeholder="From"
                        className="filter-input w-full !mt-0"
                        value={inputFromValue}
                        onChange={e => {
                          const rawValue = e.target.value;
                          setLeftScoreHistoryPanel(prev =>
                            prev ? { ...prev, yearFromInput: rawValue } : prev,
                          );
                        }}
                      />
                      <input
                        type="number"
                        min={minAllowedYear}
                        max={currentYear}
                        placeholder="To"
                        className="filter-input w-full !mt-0"
                        value={inputToValue}
                        onChange={e => {
                          const rawValue = e.target.value;
                          setLeftScoreHistoryPanel(prev =>
                            prev ? { ...prev, yearToInput: rawValue } : prev,
                          );
                        }}
                      />
                    </div>
                    <div className="mt-3">
                      <ReactSlider
                        className="relative h-9 flex items-center px-1"
                        thumbClassName="h-5 w-5 rounded-full border-2 border-blue-700 bg-white shadow-md cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-blue-300"
                        trackClassName="h-2 rounded-full"
                        min={minAllowedYear}
                        max={currentYear}
                        pearling
                        minDistance={0}
                        value={[rangeFrom, rangeTo]}
                        onChange={values => {
                          const [nextFrom, nextTo] = values.map(v => Math.trunc(Number(v)));
                          if (nextFrom > nextTo) return;
                          applyYearRange(nextFrom, nextTo);
                        }}
                        renderTrack={(props, state) => {
                          const { key, ...trackProps } = props;
                          return (
                            <div
                              key={key}
                              {...trackProps}
                              className={`${props.className} ${
                                state.index === 1
                                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 shadow-sm'
                                  : 'bg-slate-300'
                              }`}
                            />
                          );
                        }}
                      />
                      <div className="mt-1 flex items-center justify-between text-[11px] text-gray-600">
                        <span>{rangeFrom}</span>
                        <span>{rangeTo}</span>
                      </div>
                    </div>
                    <SharedButton
                      variant="secondary"
                      size="xs"
                      onClick={tryApplyInputYearRange}
                      className="ml-1 mt-3 mb-0"
                    >
                      Apply range
                    </SharedButton>
                  </div>
                  <SharedButton
                    variant="secondary"
                    size="xs"
                    onClick={tryApplyInputYear}
                    className="ml-1 mt-3 mb-0"
                  >
                    Search
                  </SharedButton>
                  <div className="mt-2">
                    <span className="inline-flex items-center rounded-full bg-violet-100 px-2.5 py-1 text-xs font-semibold text-violet-800">
                      {leftScoreHistoryPanel.selectedYearFrom != null &&
                      leftScoreHistoryPanel.selectedYearTo != null
                        ? `Score from ${leftScoreHistoryPanel.selectedYearFrom} to ${leftScoreHistoryPanel.selectedYearTo}`
                        : leftScoreHistoryPanel.selectedYear != null
                          ? `Score as of ${leftScoreHistoryPanel.selectedYear}`
                          : 'Score as of latest evidence'}
                    </span>
                  </div>
                </div>
              </>
            );
          })()}
        </aside>
      )}

      {scoreBreakdownRow && !leftInfoPanelItem && !leftSentencePanel && (
        <aside
          role="complementary"
          aria-label="Score breakdown"
          className="fixed left-0 z-40 flex flex-col border-r border-gray-200 bg-white shadow-lg overflow-hidden"
          style={{
            top: isDesktop ? NAVBAR_HEIGHT_PX : '3.3rem',
            height: isDesktop ? `calc(100vh - ${NAVBAR_HEIGHT_PX}px)` : '44vh',
            width: isDesktop ? 340 : '100vw',
            minWidth: isDesktop ? 340 : '100vw',
          }}
        >
          <ScoreBreakdownPanel rowData={scoreBreakdownRow} onClose={() => setScoreBreakdownRow(null)} />
        </aside>
      )}
      {prevalenceBreakdownRow && !leftInfoPanelItem && !leftSentencePanel && !scoreBreakdownRow && (
        <aside
          role="complementary"
          aria-label="Prevalence breakdown"
          className="fixed left-0 z-40 flex flex-col border-r border-gray-200 bg-white shadow-lg overflow-hidden"
          style={{
            top: isDesktop ? NAVBAR_HEIGHT_PX : '3.3rem',
            height: isDesktop ? `calc(100vh - ${NAVBAR_HEIGHT_PX}px)` : '44vh',
            width: isDesktop ? 340 : '100vw',
            minWidth: isDesktop ? 340 : '100vw',
          }}
        >
          <PrevalenceBreakdownPanel
            rowData={prevalenceBreakdownRow}
            onClose={() => setPrevalenceBreakdownRow(null)}
          />
        </aside>
      )}
      {evidenceTimelineRow &&
        !leftInfoPanelItem &&
        !leftSentencePanel &&
        !scoreBreakdownRow &&
        !prevalenceBreakdownRow && (
          <aside
            role="complementary"
            aria-label="Evidence timeline"
            className="fixed left-0 z-40 flex flex-col border-r border-gray-200 bg-white shadow-lg overflow-hidden"
            style={{
              top: isDesktop ? NAVBAR_HEIGHT_PX : '3.3rem',
              height: isDesktop ? `calc(100vh - ${NAVBAR_HEIGHT_PX}px)` : '44vh',
              width: isDesktop ? 340 : '100vw',
              minWidth: isDesktop ? 340 : '100vw',
            }}
          >
            <EvidenceTimelinePanel
              rowData={evidenceTimelineRow}
              onClose={() => setEvidenceTimelineRow(null)}
            />
          </aside>
        )}
      {selectedView === 'graphs' && (
        <RightAsideSidebar
          isOpen={isSidebarOpen}
          onClose={() => setSidebarOpen(false)}
          source={source}
          sourceWeights={sourceWeights}
          view={view}
          tab={tab}
          isEvidence={false}
          columnVisibilityModel={graphsColumnModel}
          setColumnVisibilityModel={updater => setGraphsColumnModel(updater)}
          searchItems={mapItemsToSearchItems(searchItems)}
          onOpenSearchItemInfo={handleOpenEntityInfo}
          onRemoveSearchItem={handleRemoveSearchItem}
          onClearAllSearchItems={() => handleClearAllSearchItems()}
          isMapping={false}
          showMyScoreSection={!isAcademic && ['GDA', 'VDA'].includes(tab as string) && isAuthenticated}
          onOpenScoreModal={() => setScoreModalOpen(true)}
          onApplySavedColumns={handleApplySavedColumns}
          onDeleteSavedColumns={handleDeleteSavedColumn}
          onApplySavedScore={handleApplySavedScore}
          onDeleteSavedScore={handleDeleteSavedScore}
          onClearWeights={handleClearWeights}
          hasActiveWeights={sourceWeights.length > 0}
          refreshScoresDropdown={refreshScoresDropdown}
        />
      )}
      {selectedView === 'tables' && (
        <RightAsideSidebar
          isOpen={isSidebarOpen}
          onClose={() => setSidebarOpen(false)}
          source={source}
          sourceWeights={sourceWeights}
          view={view}
          tab={tab}
          isEvidence={
            useMappingSidebarState
              ? (mappingSidebar.state!.columnPanelToggle?.active ?? 'summary') === 'evidence'
              : showEvidence && activeColumnPanel === 'evidence'
          }
          columnVisibilityModel={
            useMappingSidebarState
              ? mappingSidebar.state!.columnVisibilityModel
              : activeColumnPanel === 'summary' || !showEvidence
                ? (summaryColumnModel ?? {})
                : (evidenceColumnModel ?? {})
          }
          setColumnVisibilityModel={
            useMappingSidebarState
              ? mappingSidebar.state!.setColumnVisibilityModel
              : updater => {
                  if (activeColumnPanel === 'summary' || !showEvidence) {
                    setSummaryColumnModel(prev =>
                      typeof updater === 'function' ? updater(prev ?? {}) : updater,
                    );
                  } else {
                    setEvidenceColumnModel(prev =>
                      typeof updater === 'function' ? updater(prev ?? {}) : updater,
                    );
                  }
                }
          }
          columnPanelToggle={
            useMappingSidebarState
              ? mappingSidebar.state!.columnPanelToggle
              : showEvidence
                ? { active: activeColumnPanel, onChange: setActiveColumnPanel }
                : undefined
          }
          searchItems={mapItemsToSearchItems(searchItems)}
          onOpenSearchItemInfo={handleOpenEntityInfo}
          onRemoveSearchItem={handleRemoveSearchItem}
          onClearAllSearchItems={() => handleClearAllSearchItems()}
          isMapping={useMappingSidebarState}
          showMyScoreSection={!isAcademic && ['GDA', 'VDA'].includes(tab as string) && isAuthenticated}
          onOpenScoreModal={() => setScoreModalOpen(true)}
          onApplySavedColumns={handleApplySavedColumns}
          onDeleteSavedColumns={handleDeleteSavedColumn}
          onApplySavedScore={handleApplySavedScore}
          onDeleteSavedScore={handleDeleteSavedScore}
          onClearWeights={handleClearWeights}
          hasActiveWeights={sourceWeights.length > 0}
          refreshScoresDropdown={refreshScoresDropdown}
          mappingContent={
            useMappingSidebarState && currentMappingLevel?.mappingContent
              ? (currentMappingLevel.mappingContent as any)
              : mappingContent
          }
          columnOrder={columnOrder}
          isAcademic={isAcademic}
          onResetToDefault={handleResetToDefault}
        />
      )}
      <div
        className={`search-results-container ${selectedView === 'tables' ? 'flex' : ''}`}
        data-testid="search-results"
        style={{
          marginLeft:
            leftInfoPanelItem ||
            leftSentencePanel ||
            leftScoreHistoryPanel ||
            scoreBreakdownRow ||
            prevalenceBreakdownRow ||
            evidenceTimelineRow
              ? isDesktop
                ? 340
                : 0
              : 0,
          marginRight: isSidebarOpen ? (isDesktop ? sidebarWidth : 0) : 0,
          transition: 'margin-right 0.3s ease-out, margin-left 0.3s ease-out',
          paddingRight: isSidebarOpen ? '1%' : '3%',
          paddingLeft:
            leftInfoPanelItem ||
            leftSentencePanel ||
            leftScoreHistoryPanel ||
            scoreBreakdownRow ||
            prevalenceBreakdownRow ||
            evidenceTimelineRow
              ? '1%'
              : '3%',
        }}
      >
        <div
          className="pr-1 pl-1 md:p-5 md:pr-5 responsive-search-results"
          style={{ flex: '1', height: '100%', minWidth: 0 }}
        >
          <Tabs
            className="react-tabs superposed-content"
            selectedIndex={tabIndex}
            onSelect={index => handleTabSelect(index)}
          >
            <div className="responsive-search-tabs flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-2 lg:flex-nowrap">
              <div className="d-flex min-w-0 flex-1 gap-2 md:justify-start">
                <TabList className="react-tabs__tab-list col-auto">
                  {visibleTabs.map(t => (
                    <Tab key={t.key} className={'react-tabs__tab bg-white mb-0 my-2 my-lg-0'}>
                      {t.label}
                    </Tab>
                  ))}
                </TabList>
              </div>
              <div className="md:ml-auto flex min-w-0 shrink-0 items-center gap-3">
                {(searchItems?.length ?? 0) > 0 && (
                  <div className="relative flex min-w-0 flex-col items-end sm:max-w-[min(100%,40rem)] lg:max-w-[min(50%,40rem)]">
                    <div ref={searchItemsDropdownRef} className="relative w-full min-w-0">
                      <SharedButton
                        id="search-items-query-dropdown-trigger"
                        aria-expanded={searchItemsDropdownOpen}
                        variant="secondary"
                        aria-controls="search-items-query-dropdown-panel"
                        onClick={() => setSearchItemsDropdownOpen(o => !o)}
                        className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2 mb-1"
                        iconRight={
                          <ChevronDownIcon
                            className="h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200"
                            style={{
                              transform: searchItemsDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                          />
                        }
                      >
                        <span className="min-w-0 flex-1 truncate font-medium text-slate-900">
                          Showing {searchItems.length} {searchItemsEntityPlural}
                        </span>
                      </SharedButton>
                      {searchItemsDropdownOpen && (
                        <div
                          id="search-items-query-dropdown-panel"
                          role="region"
                          aria-labelledby="search-items-query-dropdown-trigger"
                          className="mr-2 absolute right-0 top-[calc(100%+0.375rem)] z-[2000] origin-top overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-xl shadow-slate-900/10 ring-1 ring-slate-900/5"
                        >
                          <div className="max-h-80 md:max-h-28 min-h-0 overflow-y-auto overscroll-contain p-3 sm:max-h-32">
                            <div className="flex flex-wrap gap-2">
                              {sortedSearchItemsForHeader.map(item => {
                                const { label } = getSearchItemBadgeLabel(item);
                                const score = getNumericScoreFromSearchItem(item);
                                return (
                                  <div key={String(item.id)} className="max-w-full min-w-0">
                                    <SharedBadge
                                      text={
                                        <span className="flex min-w-0 max-w-[18rem] items-center gap-1.5">
                                          <span className="min-w-0 flex-1 truncate">{label}</span>
                                          {score != null && (
                                            <span className="shrink-0 rounded bg-slate-200/80 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-slate-700">
                                              {score}
                                            </span>
                                          )}
                                        </span>
                                      }
                                      variant="secondary"
                                      className="!mb-0 flex h-auto min-h-7 max-w-full justify-start overflow-hidden text-left"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {!isSidebarOpen && (
                  <div className="flex shrink-0 items-center mb-2">
                    <BaseTooltip content="Open table controls" position="bottom">
                      <SharedButton
                        type="button"
                        variant="terciary"
                        onClick={() => setSidebarOpen(!isSidebarOpen)}
                        aria-expanded={isSidebarOpen}
                        aria-label={isSidebarOpen ? 'Hide table controls' : 'Open table controls'}
                        data-testid="grid-controls-sidebar-toggle"
                        size="xs"
                        iconLeft={isDesktop ? <ChevronDoubleLeftIcon className="w-5" /> : null}
                        iconMargin={null}
                        className={`p-0 mb-0 ${!isDesktop ? 'pr-3' : ''}`}
                      >
                        {!isDesktop && (
                          <>
                            <AdjustmentsHorizontalIcon className="w-5 mx-2" />
                            {isSidebarOpen ? 'Hide controls' : 'Controls'}
                          </>
                        )}
                      </SharedButton>
                    </BaseTooltip>
                  </div>
                )}
              </div>
            </div>

            {/* Score Configuration Info Message */}
            {sourceWeights.length > 0 &&
              selectedView === 'tables' &&
              ['GDA', 'VDA'].includes(tab as string) &&
              !isAcademic &&
              isAuthenticated && (
                <div className="mb-4 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg
                      className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-900">Custom Score Configuration Active</p>
                      <p className="text-xs text-blue-700 mt-1 mb-0">
                        You have a custom My Score configuration applied to this data.
                        {isAuthenticated ? (
                          <span>
                            {' '}
                            Use the "My Score" section in the Controls sidebar to manage or clear your
                            configuration.
                          </span>
                        ) : (
                          <span>
                            {' '}
                            Use the "My Score" section in the Controls sidebar to modify or clear your
                            configuration.
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

            <div className="mt-10">
              {visibleTabs.map(t => (
                <TabPanel key={t.key} className="tab-pane">
                  <>
                    {useDrillDownMapping && selectedView === 'tables' ? (
                      <MappingDrillDownProvider useDrillDown={true}>
                        <DrillDownTableContainer
                          baseLevelId={`base-${t.key}`}
                          baseLabel="Results"
                          dispatch={dispatch}
                          panelTab={t.key}
                          onOpenEntityInfo={handleOpenEntityInfo}
                          isMappingUrlHydrated={isMappingUrlHydrated}
                        >
                          <ScrollElement name="Summary">
                            {showSummary && (
                              <ManageGrid
                                source={source}
                                view={view}
                                tab={tab}
                                panelTab={t.key}
                                searchItems={mapItemsToSearchItems(searchItems)}
                                setSearchItems={setSearchItems}
                                isEvidence={false}
                                isMapping={false}
                                restrictChemical={restrictChemical}
                                isInfoSidebarOpen={isInfoSidebarOpen}
                                setIsInfoSidebarOpen={setIsInfoSidebarOpen}
                                defaultFilterValue={defaultFilterValue}
                                dispatch={dispatch}
                                sourceWeights={sourceWeights}
                                onScoreSaved={() => setRefreshScoresDropdown(prev => prev + 1)}
                                onOpenSearchItemInfo={handleOpenEntityInfo}
                                columnVisibilityModel={summaryColumnModel}
                                setColumnVisibilityModel={updaterOrValue =>
                                  setSummaryColumnModel(prev =>
                                    typeof updaterOrValue === 'function'
                                      ? updaterOrValue(prev ?? {})
                                      : updaterOrValue,
                                  )
                                }
                                columnOrder={summaryColumnOrder}
                                setColumnOrder={order => setSummaryColumnOrder(order)}
                                onColumnStateInit={(isEv, model, order) => {
                                  if (!isEv) {
                                    setSummaryColumnModel(model);
                                    setSummaryColumnOrder(order ?? undefined);
                                  }
                                }}
                                showRightSidebar={false}
                                resetRef={summaryResetRef}
                              />
                            )}
                          </ScrollElement>
                          <ScrollElement name="Evidence">
                            {showEvidence && (
                              <ManageGrid
                                view={view}
                                source={source}
                                tab={tab}
                                panelTab={t.key}
                                searchItems={mapItemsToSearchItems(searchItems)}
                                setSearchItems={setSearchItems}
                                isEvidence={true}
                                isMapping={false}
                                restrictChemical={restrictChemical}
                                setIsInfoSidebarOpen={setIsInfoSidebarOpen}
                                isInfoSidebarOpen={isInfoSidebarOpen}
                                defaultFilterValue={defaultFilterValue}
                                dispatch={dispatch}
                                sourceWeights={sourceWeights}
                                onScoreSaved={() => setRefreshScoresDropdown(prev => prev + 1)}
                                onOpenSearchItemInfo={handleOpenEntityInfo}
                                columnVisibilityModel={evidenceColumnModel}
                                setColumnVisibilityModel={updaterOrValue =>
                                  setEvidenceColumnModel(prev =>
                                    typeof updaterOrValue === 'function'
                                      ? updaterOrValue(prev ?? {})
                                      : updaterOrValue,
                                  )
                                }
                                columnOrder={evidenceColumnOrder}
                                setColumnOrder={order => setEvidenceColumnOrder(order)}
                                onColumnStateInit={(isEv, model, order) => {
                                  if (isEv) {
                                    setEvidenceColumnModel(model);
                                    setEvidenceColumnOrder(order ?? undefined);
                                  }
                                }}
                                showRightSidebar={false}
                                resetRef={evidenceResetRef}
                              />
                            )}
                          </ScrollElement>
                        </DrillDownTableContainer>
                      </MappingDrillDownProvider>
                    ) : (
                      <>
                        <ScrollElement name="Summary">
                          {selectedView === 'tables' && showSummary && (
                            <ManageGrid
                              source={source}
                              view={view}
                              tab={tab}
                              panelTab={t.key}
                              searchItems={mapItemsToSearchItems(searchItems)}
                              setSearchItems={setSearchItems}
                              isEvidence={false}
                              isMapping={false}
                              restrictChemical={restrictChemical}
                              isInfoSidebarOpen={isInfoSidebarOpen}
                              setIsInfoSidebarOpen={setIsInfoSidebarOpen}
                              defaultFilterValue={defaultFilterValue}
                              dispatch={dispatch}
                              sourceWeights={sourceWeights}
                              onScoreSaved={() => setRefreshScoresDropdown(prev => prev + 1)}
                              onOpenSearchItemInfo={handleOpenEntityInfo}
                              columnVisibilityModel={summaryColumnModel}
                              setColumnVisibilityModel={updaterOrValue =>
                                setSummaryColumnModel(prev =>
                                  typeof updaterOrValue === 'function'
                                    ? updaterOrValue(prev ?? {})
                                    : updaterOrValue,
                                )
                              }
                              columnOrder={summaryColumnOrder}
                              setColumnOrder={order => setSummaryColumnOrder(order)}
                              onColumnStateInit={(isEv, model, order) => {
                                if (!isEv) {
                                  setSummaryColumnModel(model);
                                  setSummaryColumnOrder(order ?? undefined);
                                }
                              }}
                              showRightSidebar={false}
                            />
                          )}
                          {selectedView === 'graphs' && showManageGraph && (
                            <ManageGraph
                              source={source}
                              view={view}
                              tab={tab}
                              searchItems={searchItems}
                              setSearchItems={setSearchItems}
                              sectionTitle={t.label}
                              names={searchItems}
                              title={view}
                              Graph={graphMap[t.key]}
                            />
                          )}
                        </ScrollElement>
                        <ScrollElement name="Evidence">
                          {selectedView === 'tables' && showEvidence && (
                            <ManageGrid
                              view={view}
                              source={source}
                              tab={tab}
                              panelTab={t.key}
                              searchItems={mapItemsToSearchItems(searchItems)}
                              setSearchItems={setSearchItems}
                              isEvidence={true}
                              isMapping={false}
                              restrictChemical={restrictChemical}
                              setIsInfoSidebarOpen={setIsInfoSidebarOpen}
                              isInfoSidebarOpen={isInfoSidebarOpen}
                              defaultFilterValue={defaultFilterValue}
                              dispatch={dispatch}
                              sourceWeights={sourceWeights}
                              onScoreSaved={() => setRefreshScoresDropdown(prev => prev + 1)}
                              onOpenSearchItemInfo={handleOpenEntityInfo}
                              columnVisibilityModel={evidenceColumnModel}
                              setColumnVisibilityModel={updaterOrValue =>
                                setEvidenceColumnModel(prev =>
                                  typeof updaterOrValue === 'function'
                                    ? updaterOrValue(prev ?? {})
                                    : updaterOrValue,
                                )
                              }
                              columnOrder={evidenceColumnOrder}
                              setColumnOrder={order => setEvidenceColumnOrder(order)}
                              onColumnStateInit={(isEv, model, order) => {
                                if (isEv) {
                                  setEvidenceColumnModel(model);
                                  setEvidenceColumnOrder(order ?? undefined);
                                }
                              }}
                              showRightSidebar={false}
                            />
                          )}
                        </ScrollElement>{' '}
                        <ScrollElement name="Mapping">
                          {selectedView === 'tables' && mappingContent?.isActive && (
                            <MappingResults mappingContent={mappingContent} dispatch={dispatch} />
                          )}
                        </ScrollElement>
                      </>
                    )}
                  </>
                </TabPanel>
              ))}
            </div>
          </Tabs>
        </div>
      </div>
    </>
  );
}

function SearchResults(props: SearchResultsProps) {
  return (
    <MappingSidebarColumnProvider>
      <SearchResultsInner {...props} />
    </MappingSidebarColumnProvider>
  );
}

export default SearchResults;
